import { useEffect, useState } from "react";
import { toast } from "sonner";
import {
  FileText, Copy, Download, ChevronRight, ShieldAlert, Calendar, Package, X, Loader2, Mail,
  Clock, RefreshCw, CheckCircle2, AlertCircle,
} from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import CLINIC_LEGAL_TEMPLATES from "@/data/clinicLegalTemplates";

function relativeTime(iso) {
  if (!iso) return "—";
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return "—";
  const diff = Math.max(0, Date.now() - then);
  const m = Math.floor(diff / 60000);
  if (m < 1) return "just now";
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  const d = Math.floor(h / 24);
  if (d < 30) return `${d}d ago`;
  return new Date(iso).toLocaleDateString();
}

function renderMarkdown(doc) {
  const lines = [
    `# ${doc.title}`,
    "",
    `> ${doc.subtitle}`,
    "",
    `**Document code:** ${doc.code}  `,
    `**Last revised:** ${doc.last_updated}  `,
    `**Status:** Template — not legal advice. ${doc.note}`,
    "",
    "---",
    "",
  ];
  for (const s of doc.sections) {
    lines.push(`## ${s.h}`);
    lines.push("");
    lines.push(s.b);
    lines.push("");
  }
  return lines.join("\n");
}

function renderPlaintext(doc) {
  const lines = [
    doc.title.toUpperCase(),
    doc.subtitle,
    "",
    `Document code: ${doc.code}`,
    `Last revised: ${doc.last_updated}`,
    `Status: Template — not legal advice.`,
    "",
    "================================================================",
    "",
  ];
  for (const s of doc.sections) {
    lines.push(s.h.toUpperCase());
    lines.push("----------------------------------------------------------------");
    lines.push(s.b);
    lines.push("");
  }
  return lines.join("\n");
}

function download(filename, text) {
  const blob = new Blob([text], { type: "text/markdown;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

export default function ClinicLegalTemplatesSection() {
  const [openId, setOpenId] = useState(null);
  const [packetOpen, setPacketOpen] = useState(false);
  const [packetMode, setPacketMode] = useState("download"); // "download" | "email"
  const [packetForm, setPacketForm] = useState({
    clinic_name: "",
    clinic_legal_name: "",
    signatory_name: "",
    signatory_title: "",
    effective_date: "",
    champion_email: "",
    champion_name: "",
  });
  const [packetSubmitting, setPacketSubmitting] = useState(false);
  const [history, setHistory] = useState([]);
  const [historyCounts, setHistoryCounts] = useState({ total: 0, countersigned: 0, pending: 0 });
  const [historyLoading, setHistoryLoading] = useState(false);

  const fetchHistory = async () => {
    setHistoryLoading(true);
    try {
      const res = await api.get("/admin/onboarding-packet/history?limit=20");
      setHistory(res.data?.items || []);
      setHistoryCounts(res.data?.counts || { total: 0, countersigned: 0, pending: 0 });
    } catch (e) {
      // Silent — empty history is fine on first load
      setHistory([]);
      setHistoryCounts({ total: 0, countersigned: 0, pending: 0 });
    } finally {
      setHistoryLoading(false);
    }
  };

  const toggleCountersigned = async (packet) => {
    const target = packet.signature_status !== "countersigned";
    try {
      const res = await api.patch(
        `/admin/onboarding-packet/${packet.id}/countersign`,
        { countersigned: target },
      );
      const updated = res.data?.packet;
      setHistory((prev) => prev.map((r) => (r.id === packet.id ? { ...r, ...updated } : r)));
      setHistoryCounts((prev) => ({
        total: prev.total,
        countersigned: prev.countersigned + (target ? 1 : -1),
        pending: prev.pending + (target ? -1 : 1),
      }));
      toast.success(
        target
          ? `Marked countersigned · ${packet.clinic_name}`
          : `Reverted to pending · ${packet.clinic_name}`,
      );
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  useEffect(() => {
    fetchHistory();
  }, []);

  const copy = async (doc) => {
    try {
      await navigator.clipboard.writeText(renderPlaintext(doc));
      toast.success(`${doc.code} copied to clipboard`);
    } catch {
      toast.error("Copy failed");
    }
  };

  const dl = (doc) => {
    const slug = doc.title.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "");
    download(`nodera-${slug}.md`, renderMarkdown(doc));
    toast.success(`${doc.code} downloaded`);
  };

  const generatePacket = async () => {
    if (!packetForm.clinic_name.trim()) {
      toast.error("Clinic name is required");
      return;
    }
    if (packetMode === "email" && !packetForm.champion_email.trim()) {
      toast.error("Champion email is required to send the packet");
      return;
    }
    setPacketSubmitting(true);
    try {
      const basePayload = {
        clinic_name: packetForm.clinic_name.trim(),
        clinic_legal_name: packetForm.clinic_legal_name.trim() || null,
        signatory_name: packetForm.signatory_name.trim() || null,
        signatory_title: packetForm.signatory_title.trim() || null,
        effective_date: packetForm.effective_date.trim() || null,
      };

      if (packetMode === "email") {
        const res = await api.post("/admin/onboarding-packet/send", {
          ...basePayload,
          champion_email: packetForm.champion_email.trim(),
          champion_name: packetForm.champion_name.trim() || null,
        });
        toast.success(
          `Packet emailed to ${res.data.recipient}` +
            (res.data.resend_id ? ` (id ${res.data.resend_id.slice(0, 8)}…)` : ""),
        );
        setPacketOpen(false);
        fetchHistory();
        return;
      }

      const res = await api.post(
        "/admin/onboarding-packet/generate",
        basePayload,
        { responseType: "blob" },
      );
      const blob = new Blob([res.data], { type: "application/pdf" });
      const url = URL.createObjectURL(blob);
      const slug =
        packetForm.clinic_name
          .toLowerCase()
          .replace(/[^a-z0-9]+/g, "-")
          .replace(/^-|-$/g, "") || "clinic";
      const a = document.createElement("a");
      a.href = url;
      a.download = `Nodera-Onboarding-Packet-${slug}.pdf`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      toast.success(`Onboarding packet for ${packetForm.clinic_name} downloaded`);
      setPacketOpen(false);
      fetchHistory();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setPacketSubmitting(false);
    }
  };

  return (
    <div className="space-y-4" data-testid="clinic-legal-templates">
      <div className="rounded-2xl border border-emerald-200 bg-emerald-50/60 p-4 sm:p-5 flex flex-col sm:flex-row items-start sm:items-center gap-3 sm:gap-4">
        <span className="w-10 h-10 rounded-lg bg-emerald-100 text-emerald-700 flex items-center justify-center flex-shrink-0">
          <Package className="w-4 h-4" />
        </span>
        <div className="flex-1 min-w-0">
          <h3 className="font-display text-base font-semibold text-slate-900">
            Clinic Onboarding Packet — one-click PDF
          </h3>
          <p className="text-sm text-slate-700 mt-0.5 leading-relaxed">
            Bundle all 5 documents (MSA · BAA · DPA · Stripe · AUP) into a single 18-page
            branded PDF with the clinic's name, signatory, and effective date pre-filled
            on every cover and signature block.
          </p>
        </div>
        <button
          type="button"
          onClick={() => {
            setPacketForm({
              clinic_name: "",
              clinic_legal_name: "",
              signatory_name: "",
              signatory_title: "",
              effective_date: "",
              champion_email: "",
              champion_name: "",
            });
            setPacketMode("download");
            setPacketOpen(true);
          }}
          data-testid="onboarding-packet-open-btn"
          className="inline-flex items-center gap-2 rounded-full bg-emerald-600 hover:bg-emerald-700 text-white px-4 py-2 text-sm font-semibold whitespace-nowrap"
        >
          <Package className="w-4 h-4" /> Generate packet
        </button>
      </div>

      <div className="rounded-2xl border border-amber-200 bg-amber-50/60 p-4 flex items-start gap-3">
        <ShieldAlert className="w-4 h-4 text-amber-700 mt-0.5 flex-shrink-0" />
        <div className="text-sm text-amber-900 leading-relaxed">
          <strong>Templates — not legal advice.</strong> These are operational starting points
          that codify Nodera's positioning (not a healthcare provider · does not custody funds ·
          clinic retains data ownership). A licensed healthcare attorney must review and tailor
          each document to your jurisdiction, business specifics, and risk posture before execution.
        </div>
      </div>

      <PacketHistoryPanel
        history={history}
        counts={historyCounts}
        loading={historyLoading}
        onRefresh={fetchHistory}
        onToggleCountersigned={toggleCountersigned}
      />

      <div className="grid gap-3" data-testid="legal-templates-list">
        {CLINIC_LEGAL_TEMPLATES.map((doc) => {
          const isOpen = openId === doc.id;
          return (
            <div
              key={doc.id}
              data-testid={`legal-template-${doc.id}`}
              className="rounded-2xl border border-slate-200 bg-white overflow-hidden"
            >
              <button
                type="button"
                onClick={() => setOpenId(isOpen ? null : doc.id)}
                data-testid={`legal-template-toggle-${doc.id}`}
                className="w-full flex items-start gap-4 px-5 py-4 hover:bg-slate-50 transition-colors text-left"
              >
                <span className="w-10 h-10 rounded-lg bg-violet-50 text-violet-700 flex items-center justify-center flex-shrink-0">
                  <FileText className="w-4 h-4" />
                </span>
                <div className="flex-1 min-w-0">
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="text-[10.5px] uppercase tracking-[0.18em] font-bold text-violet-700 bg-violet-50 border border-violet-200 rounded-full px-2 py-0.5">
                      {doc.code}
                    </span>
                    <h3 className="font-display text-base font-semibold text-slate-900">
                      {doc.title}
                    </h3>
                  </div>
                  <p className="text-sm text-slate-600 mt-1">{doc.subtitle}</p>
                  <p className="text-xs text-slate-500 mt-1.5 leading-relaxed">{doc.summary}</p>
                  <div className="mt-2 inline-flex items-center gap-1.5 text-[11px] text-slate-500">
                    <Calendar className="w-3 h-3" /> Last revised {doc.last_updated} · {doc.sections.length} sections
                  </div>
                </div>
                <ChevronRight
                  className={`w-4 h-4 text-slate-400 mt-2 transition-transform ${isOpen ? "rotate-90" : ""}`}
                />
              </button>

              {isOpen && (
                <div className="border-t border-slate-100 bg-slate-50/30">
                  <div className="px-5 py-3 flex flex-wrap items-center gap-2 border-b border-slate-100 bg-white">
                    <button
                      type="button"
                      onClick={() => copy(doc)}
                      data-testid={`legal-template-copy-${doc.id}`}
                      className="inline-flex items-center gap-1.5 rounded-full bg-slate-900 text-white px-3.5 py-1.5 text-xs font-semibold hover:bg-slate-800"
                    >
                      <Copy className="w-3 h-3" /> Copy plaintext
                    </button>
                    <button
                      type="button"
                      onClick={() => dl(doc)}
                      data-testid={`legal-template-download-${doc.id}`}
                      className="inline-flex items-center gap-1.5 rounded-full border border-slate-300 bg-white text-slate-700 px-3.5 py-1.5 text-xs font-semibold hover:bg-slate-50"
                    >
                      <Download className="w-3 h-3" /> Download .md
                    </button>
                    <span className="ml-auto text-[10.5px] uppercase tracking-[0.18em] font-bold text-slate-500">
                      Template
                    </span>
                  </div>
                  <div className="px-5 py-5 max-h-[70vh] overflow-y-auto" data-testid={`legal-template-body-${doc.id}`}>
                    <div className="text-xs text-slate-500 italic mb-4">{doc.note}</div>
                    {doc.sections.map((s, i) => (
                      <div key={i} className="mb-5">
                        <h4 className="font-display text-sm font-bold text-slate-900 mb-1.5">{s.h}</h4>
                        <div className="text-sm text-slate-700 leading-relaxed whitespace-pre-line">
                          {s.b}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>

      {packetOpen && (
        <div
          className="fixed inset-0 z-50 bg-slate-900/50 backdrop-blur-sm flex items-center justify-center p-3"
          onClick={() => !packetSubmitting && setPacketOpen(false)}
          data-testid="onboarding-packet-modal"
        >
          <div
            className="bg-white w-full max-w-lg rounded-2xl shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="px-5 py-4 border-b border-slate-100 flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Package className="w-4 h-4 text-emerald-700" />
                <div className="font-display text-base font-semibold text-slate-900">
                  Generate Clinic Onboarding Packet
                </div>
              </div>
              <button
                type="button"
                onClick={() => !packetSubmitting && setPacketOpen(false)}
                disabled={packetSubmitting}
                className="p-1 hover:bg-slate-100 rounded disabled:opacity-50"
                data-testid="onboarding-packet-close"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
            <div className="p-5 space-y-3">
              <p className="text-sm text-slate-600 leading-relaxed">
                Generates a single 18-page PDF bundling MSA, BAA, DPA, Stripe Terms, and AUP
                with the clinic's information pre-filled on the cover and each signature block.
              </p>

              <div
                className="grid grid-cols-2 gap-2 p-1 rounded-full bg-slate-100"
                data-testid="onboarding-packet-mode-toggle"
              >
                <button
                  type="button"
                  onClick={() => setPacketMode("download")}
                  data-testid="onboarding-packet-mode-download"
                  disabled={packetSubmitting}
                  className={`inline-flex items-center justify-center gap-1.5 rounded-full px-3 py-1.5 text-xs font-semibold transition-colors ${
                    packetMode === "download"
                      ? "bg-white text-slate-900 shadow-sm"
                      : "text-slate-600 hover:text-slate-900"
                  }`}
                >
                  <Download className="w-3.5 h-3.5" /> Download
                </button>
                <button
                  type="button"
                  onClick={() => setPacketMode("email")}
                  data-testid="onboarding-packet-mode-email"
                  disabled={packetSubmitting}
                  className={`inline-flex items-center justify-center gap-1.5 rounded-full px-3 py-1.5 text-xs font-semibold transition-colors ${
                    packetMode === "email"
                      ? "bg-white text-slate-900 shadow-sm"
                      : "text-slate-600 hover:text-slate-900"
                  }`}
                >
                  <Mail className="w-3.5 h-3.5" /> Email to Champion
                </button>
              </div>

              <Field label="Clinic name *" required>
                <input
                  data-testid="onboarding-packet-clinic-name"
                  value={packetForm.clinic_name}
                  onChange={(e) => setPacketForm({ ...packetForm, clinic_name: e.target.value })}
                  className="ipt"
                  placeholder="Bartholomew Wellness"
                />
              </Field>
              <Field label="Clinic legal name (optional — defaults to clinic name)">
                <input
                  data-testid="onboarding-packet-legal-name"
                  value={packetForm.clinic_legal_name}
                  onChange={(e) => setPacketForm({ ...packetForm, clinic_legal_name: e.target.value })}
                  className="ipt"
                  placeholder="Bartholomew Wellness LLC"
                />
              </Field>
              <div className="grid grid-cols-2 gap-3">
                <Field label="Signatory name">
                  <input
                    data-testid="onboarding-packet-signatory-name"
                    value={packetForm.signatory_name}
                    onChange={(e) => setPacketForm({ ...packetForm, signatory_name: e.target.value })}
                    className="ipt"
                    placeholder="Dr. Jane Smith"
                  />
                </Field>
                <Field label="Signatory title">
                  <input
                    data-testid="onboarding-packet-signatory-title"
                    value={packetForm.signatory_title}
                    onChange={(e) => setPacketForm({ ...packetForm, signatory_title: e.target.value })}
                    className="ipt"
                    placeholder="Managing Partner"
                  />
                </Field>
              </div>
              <Field label="Effective date (optional — defaults to today)">
                <input
                  data-testid="onboarding-packet-effective-date"
                  value={packetForm.effective_date}
                  onChange={(e) => setPacketForm({ ...packetForm, effective_date: e.target.value })}
                  className="ipt"
                  placeholder="May 22, 2026"
                />
              </Field>

              {packetMode === "email" && (
                <div
                  className="mt-2 p-4 rounded-xl border border-emerald-200 bg-emerald-50/40 space-y-3"
                  data-testid="onboarding-packet-email-fields"
                >
                  <div className="flex items-center gap-2 text-xs font-semibold text-emerald-800">
                    <Mail className="w-3.5 h-3.5" />
                    Email delivery
                  </div>
                  <Field label="Champion email *" required>
                    <input
                      type="email"
                      data-testid="onboarding-packet-champion-email"
                      value={packetForm.champion_email}
                      onChange={(e) => setPacketForm({ ...packetForm, champion_email: e.target.value })}
                      className="ipt"
                      placeholder="champion@clinic.com"
                    />
                  </Field>
                  <Field label="Champion name (optional — for personalised greeting)">
                    <input
                      data-testid="onboarding-packet-champion-name"
                      value={packetForm.champion_name}
                      onChange={(e) => setPacketForm({ ...packetForm, champion_name: e.target.value })}
                      className="ipt"
                      placeholder="Dr. Jane Smith"
                    />
                  </Field>
                </div>
              )}
            </div>
            <div className="px-5 py-3 border-t border-slate-100 flex justify-end gap-2">
              <button
                type="button"
                onClick={() => setPacketOpen(false)}
                disabled={packetSubmitting}
                className="text-sm font-semibold text-slate-700 px-4 py-2 hover:bg-slate-100 rounded-lg disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={generatePacket}
                disabled={
                  packetSubmitting ||
                  !packetForm.clinic_name.trim() ||
                  (packetMode === "email" && !packetForm.champion_email.trim())
                }
                data-testid="onboarding-packet-submit"
                className="inline-flex items-center gap-2 bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-semibold px-4 py-2 rounded-lg disabled:opacity-50"
              >
                {packetSubmitting ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    {packetMode === "email" ? "Sending email…" : "Generating PDF…"}
                  </>
                ) : packetMode === "email" ? (
                  <>
                    <Mail className="w-4 h-4" /> Send to Champion
                  </>
                ) : (
                  <>
                    <Download className="w-4 h-4" /> Generate & download
                  </>
                )}
              </button>
            </div>
            <style>{`
              .ipt { width: 100%; border: 1px solid #e2e8f0; border-radius: 8px; padding: 0.5rem 0.75rem; font-size: 14px; color: #0f172a; background: white; }
              .ipt:focus { outline: none; border-color: #10b981; box-shadow: 0 0 0 2px rgba(16,185,129,0.18); }
            `}</style>
          </div>
        </div>
      )}
    </div>
  );
}

function Field({ label, required, children }) {
  return (
    <label className="block">
      <div className="text-xs font-semibold text-slate-700 mb-1">
        {label} {required && <span className="text-emerald-600">*</span>}
      </div>
      {children}
    </label>
  );
}

function PacketHistoryPanel({ history, counts, loading, onRefresh, onToggleCountersigned }) {
  const [collapsed, setCollapsed] = useState(false);
  const safeCounts = counts || { total: 0, countersigned: 0, pending: 0 };

  return (
    <div
      className="rounded-2xl border border-slate-200 bg-white overflow-hidden"
      data-testid="packet-history-panel"
    >
      <button
        type="button"
        onClick={() => setCollapsed((v) => !v)}
        data-testid="packet-history-toggle"
        className="w-full px-5 py-3.5 flex items-center gap-3 hover:bg-slate-50 transition-colors text-left"
      >
        <span className="w-8 h-8 rounded-lg bg-slate-100 text-slate-700 flex items-center justify-center flex-shrink-0">
          <Clock className="w-3.5 h-3.5" />
        </span>
        <div className="flex-1 min-w-0">
          <div className="font-display text-sm font-semibold text-slate-900">
            Recent packets
          </div>
          <div className="text-xs text-slate-500 mt-0.5 flex flex-wrap items-center gap-x-3 gap-y-1">
            {loading ? (
              "Loading…"
            ) : history.length === 0 ? (
              "No packets generated yet."
            ) : (
              <>
                <span data-testid="packet-history-total">
                  {safeCounts.total} packet{safeCounts.total === 1 ? "" : "s"}
                </span>
                <span className="inline-flex items-center gap-1 text-emerald-700" data-testid="packet-history-countersigned-count">
                  <CheckCircle2 className="w-3 h-3" />
                  {safeCounts.countersigned} countersigned
                </span>
                <span className="inline-flex items-center gap-1 text-amber-700" data-testid="packet-history-pending-count">
                  <Clock className="w-3 h-3" />
                  {safeCounts.pending} pending
                </span>
                <span className="text-slate-400">· last {relativeTime(history[0]?.generated_at)}</span>
              </>
            )}
          </div>
        </div>
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            onRefresh();
          }}
          data-testid="packet-history-refresh"
          className="p-1.5 hover:bg-slate-200 rounded-md transition-colors"
          title="Refresh"
        >
          <RefreshCw className={`w-3.5 h-3.5 text-slate-500 ${loading ? "animate-spin" : ""}`} />
        </button>
        <ChevronRight
          className={`w-4 h-4 text-slate-400 transition-transform ${collapsed ? "" : "rotate-90"}`}
        />
      </button>

      {!collapsed && history.length > 0 && (
        <div className="border-t border-slate-100 overflow-x-auto">
          <table className="w-full text-sm" data-testid="packet-history-table">
            <thead className="bg-slate-50 text-[10.5px] uppercase tracking-[0.14em] text-slate-600">
              <tr>
                <th className="text-left px-4 py-2 font-semibold">Clinic</th>
                <th className="text-left px-4 py-2 font-semibold">Delivery</th>
                <th className="text-left px-4 py-2 font-semibold">Recipient</th>
                <th className="text-left px-4 py-2 font-semibold">Signature</th>
                <th className="text-right px-4 py-2 font-semibold">Generated</th>
              </tr>
            </thead>
            <tbody>
              {history.map((row) => {
                const isSigned = row.signature_status === "countersigned";
                return (
                  <tr key={row.id} className="border-t border-slate-100" data-testid={`packet-row-${row.id}`}>
                    <td className="px-4 py-2.5 text-slate-800">
                      <div className="font-semibold">{row.clinic_name}</div>
                      {row.clinic_legal_name && row.clinic_legal_name !== row.clinic_name && (
                        <div className="text-[11px] text-slate-500 mt-0.5">{row.clinic_legal_name}</div>
                      )}
                    </td>
                    <td className="px-4 py-2.5">
                      {row.mode === "email" ? (
                        <span
                          className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10.5px] font-bold uppercase tracking-[0.12em] ${
                            row.delivery_status === "failed"
                              ? "bg-rose-50 text-rose-700 border border-rose-200"
                              : "bg-emerald-50 text-emerald-700 border border-emerald-200"
                          }`}
                        >
                          {row.delivery_status === "failed" ? (
                            <AlertCircle className="w-3 h-3" />
                          ) : (
                            <Mail className="w-3 h-3" />
                          )}
                          {row.delivery_status === "failed" ? "Failed" : "Emailed"}
                        </span>
                      ) : (
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10.5px] font-bold uppercase tracking-[0.12em] bg-slate-50 text-slate-700 border border-slate-200">
                          <Download className="w-3 h-3" /> Download
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-2.5 text-slate-700">
                      {row.recipient_email ? (
                        <>
                          <div className="truncate max-w-[200px]">{row.recipient_email}</div>
                          {row.resend_id && (
                            <div className="text-[10.5px] text-slate-400 mt-0.5 font-mono">
                              id {row.resend_id.slice(0, 8)}…
                            </div>
                          )}
                        </>
                      ) : (
                        <span className="text-slate-400">—</span>
                      )}
                    </td>
                    <td className="px-4 py-2.5">
                      {isSigned ? (
                        <div className="flex flex-col items-start gap-1">
                          <span
                            data-testid={`packet-row-status-${row.id}`}
                            className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10.5px] font-bold uppercase tracking-[0.12em] bg-emerald-50 text-emerald-700 border border-emerald-200"
                          >
                            <CheckCircle2 className="w-3 h-3" /> Countersigned
                          </span>
                          {row.countersigned_at && (
                            <span className="text-[10.5px] text-slate-500">
                              {relativeTime(row.countersigned_at)}
                              {row.countersigned_by_admin_email
                                ? ` · by ${row.countersigned_by_admin_email.split("@")[0]}`
                                : ""}
                            </span>
                          )}
                          <button
                            type="button"
                            onClick={() => onToggleCountersigned(row)}
                            data-testid={`packet-row-undo-${row.id}`}
                            className="text-[10.5px] text-slate-500 hover:text-slate-900 underline"
                          >
                            mark pending
                          </button>
                        </div>
                      ) : (
                        <button
                          type="button"
                          onClick={() => onToggleCountersigned(row)}
                          data-testid={`packet-row-mark-countersigned-${row.id}`}
                          className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[10.5px] font-bold uppercase tracking-[0.12em] bg-white text-emerald-700 border border-emerald-300 hover:bg-emerald-50"
                        >
                          <CheckCircle2 className="w-3 h-3" /> Mark countersigned
                        </button>
                      )}
                    </td>
                    <td className="px-4 py-2.5 text-right text-slate-600 whitespace-nowrap">
                      <div>{relativeTime(row.generated_at)}</div>
                      <div className="text-[10.5px] text-slate-400 mt-0.5 truncate max-w-[140px] ml-auto">
                        {row.generated_by_admin_email || "—"}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {!collapsed && history.length === 0 && !loading && (
        <div className="border-t border-slate-100 px-5 py-6 text-center">
          <CheckCircle2 className="w-6 h-6 text-slate-300 mx-auto mb-1.5" />
          <div className="text-xs text-slate-500">
            Every packet you generate or email will be logged here for audit and follow-up.
          </div>
        </div>
      )}
    </div>
  );
}

